import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Grid, Switch, Tooltip, Typography } from "@mui/material";
import { Add, FilterList } from "@mui/icons-material";

import { PortfolioCard, useFetchPortfolios } from "../index";
import { useNavigation } from "../../../hooks/useNavigation";
import { SearchField, PaginationControls, DrawerControl, FilterSideBar } from "../../../shared/components";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { useState } from "react";
import { useFilterContext } from "../../../shared/context";
import { useInfiniteStakeholders } from "../../projects/hooks/useStakeholders";
import { useActiveFilterLabels } from "../../../shared/hooks";
import { clientTheme } from "../../../theme/clientTheme";

const cardVariant = clientTheme.layout?.portfolioCardVariant ?? "classic";

export const Portfolio = () => {

  const { isAllowedToAddPortfolio } = useCapabilities();
  const [assignedToMe, setAssignedToMe] = useState(false);

  const {
    isLoading, isError, portfolios, pagination, searchDraft, isOpenFilter,
    changePage, changeRowsPerPage, handleCloseFilter, handleOpenFilter,
    handleChangeSearch, handleClear, handleSearch
  } = useFetchPortfolios(assignedToMe);

  const { goToPortfolioProjects, goToPortfolioDetails, goToAddPortfolio } = useNavigation();

  const { hasActiveFilters, activeFilterLabels } = useActiveFilterLabels();
    
 const handleAssignedToMeChange = () => {
        setAssignedToMe(!assignedToMe);
    };

  const onViewProjects = (project: { portfolio_name: string, id: number }) => {
    goToPortfolioProjects(project.portfolio_name, project.id);
  };

  const onViewDetails = (project: { portfolio_name: string, id: number }) => {
    goToPortfolioDetails(project.portfolio_name, project.id);
  };

  if (isLoading) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
        gap={2}
      >
        <CircularProgress />
      </Box>
    )
  };

  if (isError) {
    return (
      <Box
        height="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        width="100%"
      >
        <Alert
          severity="warning"
          sx={{
            width: "auto",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          {isError}
        </Alert>
      </Box>
    )
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" gap={1.5} py={2}>
      <Box display="flex" alignItems="start" gap={2} px={2}>

        <SearchField
          label="Portfolio Name or Reference"
          searchTerm={searchDraft}
          handleSearch={handleChangeSearch}
          handleClearSearch={handleClear}
          handleApplySearch={handleSearch}
        />

        {cardVariant === "modern" ? (
          /* ── Figma (Demo) toolbar controls ─────────────────────────── */
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={assignedToMe}
                  onChange={handleAssignedToMeChange}
                  size="small"
                />
              }
              label={
                <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                  Assigned to Me
                </Typography>
              }
              sx={{ mr: 0, whiteSpace: "nowrap" }}
            />
            <Tooltip
              title={
                hasActiveFilters ? (
                  <Box sx={{ p: 0.5 }}>
                    {activeFilterLabels.map((label, i) => (
                      <Typography key={i} variant="caption" display="block">{label}</Typography>
                    ))}
                  </Box>
                ) : ""
              }
              placement="bottom-start"
              disableHoverListener={!hasActiveFilters}
              arrow
            >
              <Button
                startIcon={<FilterList />}
                variant="outlined"
                color={hasActiveFilters ? "primary" : "inherit"}
                onClick={handleOpenFilter}
                sx={{
                  whiteSpace: "nowrap",
                  borderColor: "divider",
                  color: hasActiveFilters ? "primary.main" : "text.primary",
                  textTransform: "none",
                  fontWeight: 500,
                }}
              >
                Filters{hasActiveFilters ? ` (${activeFilterLabels.length})` : ""}
              </Button>
            </Tooltip>
            {isAllowedToAddPortfolio && (
              <Button
                startIcon={<Add />}
                variant="contained"
                onClick={goToAddPortfolio}
                sx={{ marginLeft: "auto", whiteSpace: "nowrap", textTransform: "none", fontWeight: 600 }}
              >
                Add Portfolio
              </Button>
            )}
          </>
        ) : (
          /* ── Classic (Cadent) toolbar controls ──────────────────────── */
          <>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography variant="subtitle2">Assigned to Me: </Typography>
              <Switch checked={assignedToMe} onChange={handleAssignedToMeChange} />
            </Box>
            <Tooltip
              title={
                hasActiveFilters ? (
                  <Box sx={{ p: 0.5 }}>
                    {activeFilterLabels.map((label, i) => (
                      <Typography key={i} variant="caption" display="block">{label}</Typography>
                    ))}
                  </Box>
                ) : ""
              }
              placement="bottom-start"
              disableHoverListener={!hasActiveFilters}
              arrow
            >
              <Button
                startIcon={<FilterList />}
                variant={hasActiveFilters ? "contained" : "text"}
                color="inherit"
                onClick={handleOpenFilter}
                sx={
                  hasActiveFilters
                    ? {
                        backgroundColor: "action.selected",
                        color: "text.primary",
                        "&:hover": { backgroundColor: "action.hover" },
                      }
                    : undefined
                }
              >
                FILTERS{hasActiveFilters ? ` (${activeFilterLabels.length})` : ""}
              </Button>
            </Tooltip>
            {isAllowedToAddPortfolio && (
              <Button
                startIcon={<Add />}
                onClick={goToAddPortfolio}
                sx={{ marginLeft: "auto" }}
              >
                ADD PORTFOLIO
              </Button>
            )}
          </>
        )}

      </Box>

      <Box flex={1} overflow="auto" px={2} pb={2}>
        {portfolios?.length === 0 ? (
          <Box
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
          >
            <Alert
              severity="warning"
              sx={{
                width: "auto",
                justifyContent: "center",
                textAlign: "center",
              }}
            >
              {isAllowedToAddPortfolio ? "No portfolio found. You can add a new portfolio to get started." : "No portfolio found."}
            </Alert>
          </Box>
        ) : (
          <Grid
            container
            spacing={2}
            justifyContent="start"
            alignItems="stretch"
            alignContent="stretch"
          >
            {portfolios.map((
              project: {
                id: number;
                portfolio_id: number;
                portfolio_name: string;
                address: string;
                phase_template: string;
                project_status: string;
                portfolio_reference: string;
                rag_status: {
                  time: "green" | "red" | "amber";
                  cost: "green" | "red" | "amber";
                  quality: "green" | "red" | "amber";
                  overall: "green" | "red" | "amber";
                };
                projects: {
                  total_projects: number;
                  red: number;
                  amber: number;
                  green: number;
                };
              },
              index: any,
            ) => (
              <Grid key={index} size={{ sm: 12, xs: 12, md: 3, lg: 3 }}>
                <PortfolioCard
                  portfolioId={project.portfolio_id}
                  title={project.portfolio_name}
                  projectId={project.portfolio_reference}
                  projectCount={project.projects.total_projects}
                  ragStatus={project.rag_status}
                  onViewProjects={() => onViewProjects(project)}
                  onViewDetails={() => onViewDetails(project)}
                  counts={{
                    green: project.projects.green,
                    orange: project.projects.amber,
                    red: project.projects.red,
                  }}
                />
              </Grid>
            ),
            )}
          </Grid>
        )}
      </Box>

      <PaginationControls
        total={pagination.total}
        rowsPerPage={pagination.limit}
        currentPage={pagination.page}
        handlePageChange={changePage}
        handleRowsPerPage={changeRowsPerPage}
      />

      <DrawerControl
        title="Filter Records"
        subTitle="Please apply following filters to refine results"
        isOpen={isOpenFilter}
        handleClose={handleCloseFilter}
      >
        <FilterSideBar onClose={handleCloseFilter} />
      </DrawerControl>
    </Box>
  );
}